import { uniq } from 'lodash'

import { DeploymentDto, StructureUserPermissionDto } from '@/endpoints/models'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { NativeMap } from '@/utils/collections'

import { Actions } from './actions'
import {
	NODE_ADD,
	NODE_ADD_SYSTEM,
	NODE_COLLAPSE,
	NODE_DELETE,
	NODE_DESELECT,
	NODE_EXPAND,
	NODE_IMPORT_STATE,
	NODE_LOAD,
	NODE_LOAD_CHILDREN,
	NODE_LOAD_HISTORY_VERSION,
	NODE_LOAD_NESTED_CHILDREN,
	NODE_LOAD_PERMISSIONS,
	NODE_LOAD_SYSTEM,
	NODE_LOCK,
	NODE_SELECT,
	NODE_SELECT_MODE_CHANGE,
	NODE_SET_SELECTED,
	NODE_UNLOCK,
} from './constants'
import { mergeStructureNodeFromBulkLoad, removeDeletedNodes } from './helpers'

export type State = Readonly<typeof initialState>
export type NodeSession = {
	expanded: State['expanded']
}

export const getNodeSession = (state: State) => ({
	expanded: state.expanded,
})

export type StructureDtoRedux = StructureDto & {
	data?: string
	deploymentDto?: DeploymentDto
	workingData?: string
}

const initialState = {
	nodes: {} as NativeMap<StructureDtoRedux>,
	roots: [] as number[],
	children: {} as NativeMap<number[]>,
	expanded: [] as number[],
	permissions: {} as NativeMap<StructureUserPermissionDto[]>,
	selectedNodes: [] as number[],
	selectMode: false as boolean,
}

export default (state = initialState, action: Actions): State => {
	switch (action.type) {
		case NODE_LOAD_SYSTEM: {
			const nodes = { ...state.nodes }
			for (const item of action.payload) {
				const id = item.id as number
				nodes[id] = mergeStructureNodeFromBulkLoad(nodes[id], item)
			}
			const roots = action.payload.map((x: StructureDto) => Number(x.id))

			return {
				...state,
				nodes,
				roots,
			}
		}

		case NODE_LOAD_CHILDREN: {
			const nodes = { ...state.nodes }
			for (const item of action.payload) {
				const id = item.id as number
				nodes[id] = mergeStructureNodeFromBulkLoad(nodes[id], item)
			}

			return {
				...state,
				nodes,
				children: {
					...state.children,
					[action.metadata.parentId]: action.payload.map(
						(x: StructureDto) => x.id,
					),
				},
			}
		}

		case NODE_LOAD_NESTED_CHILDREN: {
			const nodes = { ...state.nodes }
			for (const item of action.payload) {
				const id = item.id as number
				nodes[id] = mergeStructureNodeFromBulkLoad(nodes[id], item)
			}

			// Build children map in O(N) instead of O(N²) spread-per-node
			const newChildrenByParent: Record<string, number[]> = {}
			const isFullTree = action.payload.some(
				(node) => node.parentStructureId === undefined,
			)

			for (const node of action.payload) {
				if (node.parentStructureId === undefined) continue
				const parentId = String(node.parentStructureId)
				if (!newChildrenByParent[parentId]) newChildrenByParent[parentId] = []
				newChildrenByParent[parentId].push(node.id)
			}

			// Full tree: replace children entirely so deleted nodes are removed
			// Partial load: merge into existing so other parents are preserved
			const children: NativeMap<number[]> = isFullTree
				? {}
				: { ...state.children }

			for (const [parentId, ids] of Object.entries(newChildrenByParent)) {
				children[parentId] = uniq(ids)
			}

			return {
				...state,
				nodes,
				children,
			}
		}

		case NODE_LOAD: {
			return {
				...state,
				nodes: { ...state.nodes, [action.payload.id]: action.payload },
			}
		}

		case NODE_LOAD_HISTORY_VERSION: {
			return {
				...state,
				nodes: {
					...state.nodes,
					[action.payload.structureId]: {
						...(state.nodes[action.payload.structureId] ?? {}),
						...action.payload,
						id: action.payload.structureId,
					},
				},
			}
		}

		case NODE_ADD: {
			const id = Number(action.payload.id)
			const parentId = action.payload.parentStructureId as number
			const parent = state.nodes[parentId]

			return {
				...state,
				nodes: {
					...state.nodes,
					[id]: action.payload,
					[parentId]: { ...parent, hasChildren: true },
				},
				children: {
					...state.children,
					[parentId]: (state.children[parentId] || []).concat(id),
				},
			}
		}

		case NODE_ADD_SYSTEM: {
			const id = Number(action.payload.id)

			return {
				...state,
				nodes: {
					...state.nodes,
					[id]: action.payload,
				},
				roots: state.roots.concat(id),
			}
		}

		case NODE_EXPAND: {
			const expanded = state.expanded.includes(action.key)
				? state.expanded
				: state.expanded.concat([action.key])

			return {
				...state,
				expanded,
			}
		}

		case NODE_COLLAPSE: {
			const expanded = state.expanded.filter((id) => id !== action.key)

			return {
				...state,
				expanded,
			}
		}

		case NODE_DELETE: {
			const { nodes, children } = removeDeletedNodes(
				action.metadata.node,
				state,
			)

			return {
				...state,
				nodes,
				roots: state.roots.filter((x) => x !== action.metadata.node.id),
				children,
			}
		}

		case NODE_LOCK: {
			const { node, userId, userName } = action.metadata

			if (!node.id) return state

			return {
				...state,
				nodes: {
					...state.nodes,
					[node.id]: {
						...node,
						lockTime: new Date().toISOString(),
						lockUserId: userId,
						lockUserName: userName,
					},
				},
			}
		}

		case NODE_UNLOCK: {
			const { node } = action.metadata

			if (!node.id) return state

			return {
				...state,
				nodes: {
					...state.nodes,
					[node.id]: {
						...action.metadata.node,
						lockTime: null,
						lockUserId: null,
						lockUserName: null,
					},
				},
			}
		}

		case NODE_IMPORT_STATE: {
			const saved = action.session

			return {
				...state,
				expanded: Array.isArray(saved.expanded) ? saved.expanded : [],
			}
		}

		case NODE_LOAD_PERMISSIONS: {
			return {
				...state,
				permissions: {
					...state.permissions,
					[action.metadata.nodeId]: action.payload,
				},
			}
		}

		case NODE_SELECT: {
			return {
				...state,
				selectedNodes: [
					...state.selectedNodes,
					...action.metadata.selectedNodes,
				].filter((v, i, a) => a.indexOf(v) === i),
			}
		}

		case NODE_DESELECT: {
			return {
				...state,
				selectedNodes: [
					...state.selectedNodes.filter(
						(nodeId) => !action.metadata.deselectedNodes.includes(nodeId),
					),
				],
			}
		}

		case NODE_SET_SELECTED: {
			return {
				...state,
				selectedNodes: action.metadata.selectedNodes,
			}
		}

		case NODE_SELECT_MODE_CHANGE: {
			return {
				...state,
				selectMode: action.metadata.selectMode,
				selectedNodes: [],
			}
		}

		default:
			return state
	}
}
